import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../utils/axiosInstance";
import PageContainer from "../layouts/PageContainer";
import {
  Card,
  EmptyState,
  ErrorBanner,
  LoadingPanel,
  PageHeader,
  RefreshButton,
} from "../ui";
import ReportsDateFilter from "./ReportsDateFilter";
import ReportsSubNav from "./ReportsSubNav";
import { ErrorsReportPayload } from "./reportsTypes";

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split("T")[0];
}

export default function ErrorsReport() {
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [data, setData] = useState<ErrorsReportPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<ErrorsReportPayload>("/reports/errors", {
        params: { from: f, to: t },
      });
      setData(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to load errors report.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRange = (f: string, t: string) => {
    setFrom(f);
    setTo(t);
    load(f, t);
  };

  const worstLocationId = data && data.byStore.length ? data.byStore[0].locationId : null;
  const maxCount = data && data.byStore.length ? data.byStore[0].errorCount : 0;

  return (
    <PageContainer>
      <PageHeader
        title="Errors by store"
        subtitle={data ? `${data.range.from} to ${data.range.to}` : undefined}
        actions={<RefreshButton onClick={() => load(from, to)} loading={loading} />}
      />

      <div className="sq-stack">
        <ReportsSubNav />
        <ReportsDateFilter from={from} to={to} onApply={applyRange} loading={loading} />

        {error ? <ErrorBanner onRetry={() => load(from, to)}>{error}</ErrorBanner> : null}
        {loading && !data ? (
          <Card>
            <LoadingPanel label="Loading errors" />
          </Card>
        ) : null}

        {data ? (
          <div className="sq-columns">
            <Card
              flush
              title="Most error-prone stores"
              subtitle="Machine fault and error log count per store, ranked worst first."
              aria-label="Errors by store"
            >
              {data.byStore.length === 0 ? (
                <EmptyState title="No errors logged in this range" />
              ) : (
                <div className="sq-table-wrap">
                  <table className="sq-table is-stacked">
                    <thead>
                      <tr>
                        <th scope="col">Store</th>
                        <th scope="col" className="num">Errors</th>
                        <th scope="col">Relative</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byStore.map((row) => (
                        <tr
                          key={row.locationId}
                          className={row.locationId === worstLocationId ? "is-flagged" : undefined}
                        >
                          <td>
                            <div className="sq-strong">{row.name}</div>
                            {row.locationPin ? (
                              <div className="sq-muted">{row.locationPin}</div>
                            ) : null}
                          </td>
                          <td className="num sq-strong" data-label="Errors">
                            {row.errorCount}
                          </td>
                          <td className="cell-wide" style={{ minWidth: "8rem" }}>
                            <span className="sq-meter" aria-hidden="true">
                              <i
                                style={{
                                  width: `${maxCount > 0 ? (row.errorCount / maxCount) * 100 : 0}%`,
                                }}
                              />
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            <Card
              flush
              title="Top error types"
              subtitle="Most common fault codes across all stores."
              aria-label="Top error types"
            >
              {data.topErrorTypes.length === 0 ? (
                <EmptyState compact title="None recorded" />
              ) : (
                <div className="sq-table-wrap">
                  <table className="sq-table">
                    <thead>
                      <tr>
                        <th scope="col">Error</th>
                        <th scope="col" className="num">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topErrorTypes.map((row, idx) => (
                        <tr key={idx}>
                          <td>
                            {row.errorCode ? `${row.errorCode}` : ""}
                            {row.errorCode && row.errorName ? " — " : ""}
                            {row.errorName || (!row.errorCode ? "Unknown" : "")}
                          </td>
                          <td className="num">{row.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        ) : null}
      </div>
    </PageContainer>
  );
}
